import { Button } from '@1trading/react/components/ui/button';
import { FormStepper, useFormStepper } from '@1trading/react/components/ui/form-stepper';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { useProgress } from '@/context/progress';
import {
  AML_QUESTIONNAIRE_DEFAULTS,
  type AmlQuestionnaire,
  amlQuestionnaireCryptoDepositSchema,
  amlQuestionnaireEmploymentDetailsSchema,
  amlQuestionnaireEmploymentStatusSchema,
  amlQuestionnaireFiatDepositSchema,
  amlQuestionnaireInvestmentSchema,
  amlQuestionnairePepSchema,
  amlQuestionnairePurposeAndFundsSchema,
  amlQuestionnaireTradingVolumeSchema,
  amlQuestionnaireWithdrawalSchema,
  amlQuestionnaireWithIdentitySchema,
} from '@/features/onboarding/aml-questionnaire/schema';
import { useProfileAmlMutation } from '@/hooks/queries/use-profile-aml';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { AmlRiskLevel } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { AmlQuestionnaireFieldsetCryptoDeposit, AmlQuestionnaireFieldsetCryptoDepositLegend } from './fieldsets/crypto-deposit';
import { AmlQuestionnaireFieldsetEmploymentDetails, AmlQuestionnaireFieldsetEmploymentDetailsLegend } from './fieldsets/employment-details';
import { AmlQuestionnaireFieldsetEmploymentStatus, AmlQuestionnaireFieldsetEmploymentStatusLegend } from './fieldsets/employment-status';
import { AmlQuestionnaireFieldsetFiatDeposit, AmlQuestionnaireFieldsetFiatDepositLegend } from './fieldsets/fiat-deposit';
import { AmlQuestionnaireFieldsetInvestment, AmlQuestionnaireFieldsetInvestmentLegend } from './fieldsets/investment';
import { AmlQuestionnaireFieldsetPep, AmlQuestionnaireFieldsetPepLegend } from './fieldsets/pep';
import { AmlQuestionnaireFieldsetPurposeAndFunds, AmlQuestionnaireFieldsetPurposeAndFundsLegend } from './fieldsets/purpose-and-funds';
import { AmlQuestionnaireFieldsetTradingVolume, AmlQuestionnaireFieldsetTradingVolumeLegend } from './fieldsets/trading-volume';
import { AmlQuestionnaireFieldsetWithdrawal, AmlQuestionnaireFieldsetWithdrawalLegend } from './fieldsets/withdrawal';

const FORM_STEPS = {
  PEP: {
    Component: AmlQuestionnaireFieldsetPep,
    Legend: AmlQuestionnaireFieldsetPepLegend,
    schema: amlQuestionnairePepSchema,
  },
  EMPLOYMENT_STATUS: {
    Component: AmlQuestionnaireFieldsetEmploymentStatus,
    Legend: AmlQuestionnaireFieldsetEmploymentStatusLegend,
    schema: amlQuestionnaireEmploymentStatusSchema,
  },
  EMPLOYMENT_DETAILS: {
    Component: AmlQuestionnaireFieldsetEmploymentDetails,
    Legend: AmlQuestionnaireFieldsetEmploymentDetailsLegend,
    schema: amlQuestionnaireEmploymentDetailsSchema,
  },
  INVESTMENT: {
    Component: AmlQuestionnaireFieldsetInvestment,
    Legend: AmlQuestionnaireFieldsetInvestmentLegend,
    schema: amlQuestionnaireInvestmentSchema,
  },
  WITHDRAWAL: {
    Component: AmlQuestionnaireFieldsetWithdrawal,
    Legend: AmlQuestionnaireFieldsetWithdrawalLegend,
    schema: amlQuestionnaireWithdrawalSchema,
  },
  FIAT_DEPOSIT: {
    Component: AmlQuestionnaireFieldsetFiatDeposit,
    Legend: AmlQuestionnaireFieldsetFiatDepositLegend,
    schema: amlQuestionnaireFiatDepositSchema,
  },
  CRYPTO_DEPOSIT: {
    Component: AmlQuestionnaireFieldsetCryptoDeposit,
    Legend: AmlQuestionnaireFieldsetCryptoDepositLegend,
    schema: amlQuestionnaireCryptoDepositSchema,
  },
  TRADING_VOLUME: {
    Component: AmlQuestionnaireFieldsetTradingVolume,
    Legend: AmlQuestionnaireFieldsetTradingVolumeLegend,
    schema: amlQuestionnaireTradingVolumeSchema,
  },
  PURPOSE_AND_FUNDS: {
    Component: AmlQuestionnaireFieldsetPurposeAndFunds,
    Legend: AmlQuestionnaireFieldsetPurposeAndFundsLegend,
    schema: amlQuestionnairePurposeAndFundsSchema,
  },
} satisfies Record<string, FormStep<AmlQuestionnaire>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const AmlQuestionnaireFormCurrentStep = () => {
  const { setProgress } = useProgress();
  const { currentStep, totalSteps } = useFormStepper();
  const Step = STEPS[currentStep];

  useEffect(() => {
    return setProgress(currentStep / totalSteps);
  }, [currentStep, setProgress, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
AmlQuestionnaireFormCurrentStep.displayName = 'AmlQuestionnaireFormCurrentStep';

const AmlQuestionnaireFormCurrentStepLegend = () => {
  const { currentStep } = useFormStepper();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
AmlQuestionnaireFormCurrentStepLegend.displayName = 'AmlQuestionnaireFormCurrentStepLegend';

const AmlQuestionnaireFormBackButton = () => {
  const { currentStep, prevStep } = useFormStepper();

  const handlePrevStep = useCallback(() => prevStep(), [prevStep]);

  if (currentStep === 0) return null;

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
AmlQuestionnaireFormBackButton.displayName = 'AmlQuestionnaireFormBackButton';

const AmlQuestionnaireFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, isLastStep, nextStep } = useFormStepper();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t(isLastStep ? 'buttons.submit' : 'buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
AmlQuestionnaireFormFooter.displayName = 'AmlQuestionnaireFormFooter';

const AmlQuestionnaireForm = () => {
  const { isPending, mutate } = useProfileAmlMutation();
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation, set: setVerificationInformation } = useProfileVerificationInformation();
  const isLoading = isLoadingIdentity || isLoadingVerificationInformation;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onError = () => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: AmlQuestionnaire) => {
    try {
      const amlAnswers = await amlQuestionnaireWithIdentitySchema.parseAsync({
        ...data,
        birthCountry: identity?.birthInfo?.country,
        birthDate: identity?.birthInfo?.date,
        residencyCountry: identity?.addressOfResidency?.country,
      });

      mutate(
        { answers: amlAnswers },
        {
          onError,
          onSuccess: (data) => {
            setVerificationInformation({ aml: { date: new Date().toISOString() } });
            navigate(data.riskLevel === AmlRiskLevel.PROHIBITED ? '/onboarding/aml-questionnaire/ineligible' : '/onboarding/complete', { replace: true });
          },
        },
      );
    } catch {
      onError();
    }
  };

  if (isLoading) return <LoadingCard />;

  if (!isCompleteIdentity) return <Navigate to="/onboarding/personal-information" replace />;

  if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED') return <Navigate to="/onboarding/verification" replace />;

  if (verificationInformation.aml.date) return <Navigate to="/onboarding/complete" replace />;

  return (
    <FormStepper className="relative" defaultValues={AML_QUESTIONNAIRE_DEFAULTS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <AmlQuestionnaireFormCurrentStepLegend />
        <AmlQuestionnaireFormBackButton />
        <PageCard size="lg" bordered fixedFooter>
          <AmlQuestionnaireFormCurrentStep />
          <AmlQuestionnaireFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </FormStepper>
  );
};
AmlQuestionnaireForm.displayName = 'AmlQuestionnaireForm';

export default AmlQuestionnaireForm;
